import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { init, setUser } from '@servicetitan/datadog-rum';
import {
  configureJourney,
  normalizeTagValue,
  setJourneyDefaultEnabled,
} from '@servicetitan/journey';
import environment from '@lib/environment';

import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';
import FullStoryModule from '@modules/FullStoryModule';

/**
 * RUM `service` for this app. It equals the RUM application name, which by
 * Frontend Platform convention marks the host. Embedded MFEs register their
 * own services on the same RUM session and must never call `init` with an
 * `application`.
 */
export const DATADOG_RUM_SERVICE = 'ccpro';

/** RUM `context.project` for every CC Pro event. Agreed with SRE for CUFs. */
export const DATADOG_RUM_PROJECT = 'ccpro';

declare global {
  interface Window {
    /** Injected by the ServiceTitan synthetics runner (OPST-1676). */
    __syntheticMeta?: unknown;
  }
}

export type DatadogErrorContext = Record<string, unknown>;

const ACCESS_TOKEN_PARAM = /([?&]access_token=)[^&#\s]*/gi;

function redactUrl(url: string): string {
  return url.replace(ACCESS_TOKEN_PARAM, '$1[REDACTED]');
}

/**
 * RUM `beforeSend`: strips `access_token` query params before events leave
 * the browser. Pre-signed recording, voicemail and media URLs carry a token
 * that grants access to the file for 24 hours, and the RTCS SignalR negotiate
 * request carries the user's JWT the same way.
 */
export function redactAccessTokens(event: RumEvent): boolean {
  if (event.type === 'resource') {
    event.resource.url = redactUrl(event.resource.url);
  } else if (event.type === 'error') {
    event.error.message = redactUrl(event.error.message);
    // A stack trace repeats the message on its first line.
    if (event.error.stack) {
      event.error.stack = redactUrl(event.error.stack);
    }
    if (event.error.handling_stack) {
      event.error.handling_stack = redactUrl(event.error.handling_stack);
    }
    if (event.error.resource) {
      event.error.resource.url = redactUrl(event.error.resource.url);
    }
  }
  return true;
}

/**
 * Turns on `@servicetitan/journey` (Critical User Flows) once RUM runs, and
 * instruments `fetch`, which Apollo and the BFF session calls use. Journeys
 * only score requests they stamp with `step.stamp()`: SIGNIN starts a lot of
 * unrelated GraphQL traffic that auto-attribution would pin on whichever
 * step happens to be in flight.
 */
function enableJourneys(): void {
  setJourneyDefaultEnabled(true);
  configureJourney({
    fetch: true,
    autoAttributeRequests: false,
    // Same threshold as the monolith; the package default is 4 s.
    slowRequests: { defaultMs: 10_000 },
  });
}

/**
 * Datadog Real User Monitoring, through the `@servicetitan/datadog-rum`
 * wrapper. This app is the RUM host: it owns the SDK init, and the wrapper
 * guards `window.DD_RUM` so MFEs cannot replace the instance.
 */
export class DatadogModule extends AppModule {
  override preload(): void {
    this.app.events.on('app.started', () => {
      if (
        !environment.DATADOG_RUM_APPLICATION_ID ||
        !environment.DATADOG_RUM_CLIENT_TOKEN
      ) {
        return;
      }

      init({
        // When false, the wrapper skips the SDK start but still installs
        // `window.datadogRumForDev` so a developer can turn RUM on locally.
        enabled: environment.DATADOG_RUM_ENABLED,
        service: {
          name: DATADOG_RUM_SERVICE,
          // Sends W3C `traceparent` to the BFF so RUM requests link to its
          // OpenTelemetry traces. The BFF uses a parent-based sampler, so
          // `traceSampleRate` below decides which of these traces it exports.
          allowedTracingUrls: { tracecontext: [environment.API_URL] },
        },
        application: {
          applicationId: environment.DATADOG_RUM_APPLICATION_ID,
          clientToken: environment.DATADOG_RUM_CLIENT_TOKEN,
          site: 'datadoghq.com',
          env: environment.DATADOG_RUM_ENV,
          version: environment.APP_VERSION,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 0,
          // The BFF exports 100% of traces today; keep it that way.
          traceSampleRate: 100,
          trackResources: true,
          trackUserInteractions: true,
          trackLongTasks: true,
          defaultPrivacyLevel: 'mask-user-input',
          // Long-lived third-party traffic would otherwise keep views "loading".
          excludedActivityUrls: [
            /fullstory\.com/,
            /pendo\.io/,
            /braze\.com/,
            /launchdarkly\.com/,
          ],
          beforeSend: redactAccessTokens,
        },
      });

      if (this.isActive()) {
        // CUF Manager selects CC Pro journeys by `context.project`.
        datadogRum.setGlobalContextProperty('project', DATADOG_RUM_PROJECT);
        const isCypress = 'Cypress' in window;
        const isSyntheticsRunner = window.__syntheticMeta !== undefined;
        datadogRum.setGlobalContextProperty(
          'isSynthetic',
          isCypress || isSyntheticsRunner,
        );
        enableJourneys();
        this.app.logger.info('initialized');
      }
    });

    this.app.events.on(AuthEvents.SIGNIN, (user: AuthUser) => {
      if (this.isActive()) {
        setUser({
          id: user.id,
          name: user.name,
          // The CUF metric reads the tenant from `usr.tenant`. A CC Pro user
          // belongs to a network of tenants, so the network stands in for it.
          tenant: normalizeTagValue(user.org.name),
          tenantId: user.org.id,
          orgId: user.org.id,
          orgName: user.org.name,
          contactCenterId: user.contactCenterId,
          accountId: user.accountId,
          fullStoryUrl: FullStoryModule.sessionUrl,
        });
        this.app.logger.info('user identified: ', user.id);
      }
    });

    this.app.events.on(AuthEvents.SIGNOUT, () => {
      if (this.isActive()) {
        datadogRum.clearUser();
      }
    });
  }

  /** Reports a handled error to RUM. No-op while RUM is not running. */
  public addError(error: Error, context?: DatadogErrorContext): void {
    if (this.isActive()) {
      datadogRum.addError(error, context);
    }
  }

  private isActive(): boolean {
    return datadogRum.getInitConfiguration() !== undefined;
  }
}

export default new DatadogModule(
  appManager.events,
  appManager.logger('Datadog'),
);
